<template>
  <div>
    <v-row class="mb-2" align="center">
      <v-col cols="12" sm="6">
        <h2>
          Timesheets
          <v-chip v-if="pendingCount" size="small" color="warning" class="ml-2">{{ pendingCount }} to review</v-chip>
        </h2>
        <p class="text-caption text-medium-emphasis">Actual time worked — clocked in by crew or entered here.</p>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <v-btn size="small" color="primary" :disabled="locked || committedBookings.length === 0" @click="openAdd">
          Add Time
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="locked" type="info" variant="tonal" density="compact" class="mb-3">
      This gig is {{ gig.state.toLowerCase() }} — its timesheets are locked.
    </v-alert>
    <v-alert v-if="error || store.error" type="error" closable class="mb-3" @click:close="dismissError">
      {{ error || store.error }}
    </v-alert>
    <v-alert v-for="e in staleEntries" :key="e.id" type="warning" variant="tonal" density="compact" class="mb-2">
      {{ personName(e) }} has been clocked in since {{ formatDateTime(e.clockIn) }} — likely forgot to clock out.
      <template v-if="!locked" #append>
        <v-btn size="small" variant="text" @click="openEdit(e)">Fix</v-btn>
      </template>
    </v-alert>

    <!-- Actual cost: approved time only; pending shown as what it'd add. -->
    <v-card v-if="entries.length > 0" variant="tonal" class="mb-3" data-testid="actual-summary">
      <v-card-text class="d-flex flex-wrap align-center py-3" style="gap: 24px">
        <div>
          <div class="text-caption text-medium-emphasis">Approved hours</div>
          <div class="text-h6">{{ actuals.totals.approvedHours.toFixed(2) }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Actual cost</div>
          <div class="text-h6" :class="actuals.totals.overBudget ? 'text-error' : ''">${{ actuals.totals.approvedCost.toLocaleString() }}</div>
        </div>
        <div v-if="actuals.totals.projectedCost > actuals.totals.approvedCost">
          <div class="text-caption text-medium-emphasis">If pending approved</div>
          <div class="text-h6" :class="actuals.totals.projectedOverBudget ? 'text-error' : 'text-warning'">
            ${{ actuals.totals.projectedCost.toLocaleString() }}
          </div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Budget</div>
          <div class="text-h6">${{ actuals.totals.budgeted.toLocaleString() }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Offered (scheduled)</div>
          <div class="text-h6">${{ actuals.totals.scheduledCost.toLocaleString() }}</div>
        </div>
        <v-chip v-if="actuals.totals.overBudget" color="error" size="small">
          ${{ (actuals.totals.approvedCost - actuals.totals.budgeted).toLocaleString() }} over budget
        </v-chip>
      </v-card-text>
      <v-table density="compact" class="bg-transparent">
        <thead>
          <tr>
            <th>Person</th>
            <th class="text-right">Approved hrs</th>
            <th class="text-right">Actual</th>
            <th class="text-right">Pending hrs</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in personRollup" :key="row.bookingId">
            <td>{{ row.name }}<span class="text-caption text-medium-emphasis"> — {{ row.role }}</span></td>
            <td class="text-right">{{ row.approvedHours.toFixed(2) }}</td>
            <td class="text-right">${{ row.approvedCost.toLocaleString() }}</td>
            <td class="text-right">{{ row.pendingHours ? row.pendingHours.toFixed(2) : '—' }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-card>
      <v-card-text v-if="entries.length === 0" class="text-center py-8 text-medium-emphasis">
        No time recorded for this gig yet
      </v-card-text>
      <v-table v-else density="compact">
        <thead>
          <tr>
            <th>Person</th>
            <th>Call</th>
            <th>In</th>
            <th>Out</th>
            <th class="text-right">Hours</th>
            <th>Status</th>
            <th v-if="!locked" class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in entries" :key="e.id">
            <td>
              {{ personName(e) }}
              <div class="text-caption text-medium-emphasis">{{ roleName(e) }}</div>
            </td>
            <td>
              <template v-if="callFor(e)">
                {{ callFor(e)!.type }}
                <div class="text-caption text-medium-emphasis">
                  {{ formatDateTime(callFor(e)!.startTime) }} → {{ formatTime(callFor(e)!.endTime) }}
                </div>
              </template>
              <span v-else class="text-medium-emphasis">—</span>
            </td>
            <td>
              {{ formatDateTime(e.clockIn) }}
              <div v-if="e.originalClockIn" class="text-caption text-medium-emphasis">
                punched {{ formatTime(e.originalClockIn) }}
              </div>
            </td>
            <td>
              {{ e.clockOut ? formatDateTime(e.clockOut) : '—' }}
              <div v-if="e.originalClockOut" class="text-caption text-medium-emphasis">
                punched {{ formatTime(e.originalClockOut) }}
              </div>
            </td>
            <td class="text-right">
              {{ e.clockOut ? entryHours(e).toFixed(2) : '—' }}
              <div v-if="e.breakMinutes" class="text-caption text-medium-emphasis">{{ e.breakMinutes }}m break</div>
            </td>
            <td>
              <v-chip size="small" :color="statusColor(e.status)">{{ statusLabel(e.status) }}</v-chip>
              <v-icon v-if="e.source === 'manual'" size="small" class="ml-1" title="Entered manually">mdi-pencil</v-icon>
              <div v-if="e.status === 'REJECTED' && e.reviewNote" class="text-caption text-error">{{ e.reviewNote }}</div>
              <div v-if="e.notes" class="text-caption text-medium-emphasis">{{ e.notes }}</div>
            </td>
            <td v-if="!locked" class="text-right text-no-wrap">
              <template v-if="e.status === 'PENDING'">
                <v-btn size="x-small" color="success" variant="tonal" class="mr-1" :disabled="store.busy || cannotReviewOwn(e)" @click="approve(e)">Approve</v-btn>
                <v-btn size="x-small" color="error" variant="tonal" class="mr-1" :disabled="store.busy || cannotReviewOwn(e)" @click="openReject(e)">Reject</v-btn>
              </template>
              <v-btn v-if="e.status === 'PENDING' || e.status === 'OPEN'" size="x-small" variant="text" :disabled="cannotReviewOwn(e)" @click="openEdit(e)">
                {{ e.status === 'OPEN' ? 'Close out' : 'Edit' }}
              </v-btn>
              <v-btn v-if="isManager && e.status === 'APPROVED'" size="x-small" variant="text" :disabled="store.busy" @click="reopen(e)">Reopen</v-btn>
              <v-btn v-if="isManager" size="x-small" variant="text" color="error" :disabled="store.busy" @click="confirmDelete(e)">Delete</v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- Add / edit -->
    <v-dialog v-model="formOpen" max-width="520">
      <v-card>
        <v-card-title>{{ editing ? 'Edit time' : 'Add time' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="formError" type="error" density="compact" class="mb-3">{{ formError }}</v-alert>
          <v-select
            v-model="form.bookingId"
            :items="bookingItems"
            item-title="title"
            item-value="value"
            label="Person"
            :disabled="!!editing"
            @update:model-value="form.callId = undefined"
          />
          <v-select
            v-model="form.callId"
            :items="callItems"
            item-title="title"
            item-value="value"
            label="Call (optional)"
            clearable
            :disabled="!!editing"
            hint="Picking a call fills in its start and end — adjust them if the actual time differed."
            persistent-hint
            @update:model-value="fillFromCall"
          />
          <v-text-field v-model="form.clockIn" type="datetime-local" label="Clock in" class="mt-3" />
          <v-text-field v-model="form.clockOut" type="datetime-local" label="Clock out" :hint="clockOutHint" persistent-hint :error="clockOutInFuture" />
          <v-text-field v-model.number="form.breakMinutes" type="number" min="0" step="5" label="Break (minutes)" />
          <v-textarea v-model="form.notes" label="Notes" rows="2" auto-grow maxlength="1000" counter="1000" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="formOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="store.busy" @click="saveForm">{{ editing ? 'Save' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject -->
    <v-dialog v-model="rejectOpen" max-width="420">
      <v-card>
        <v-card-title>Reject entry</v-card-title>
        <v-card-text>
          <v-textarea v-model="rejectNote" label="Reason (required)" rows="2" auto-grow maxlength="1000" />
          <p class="text-caption text-medium-emphasis">A rejected entry is final — add a manual entry for the correct time.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="rejectOpen = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :disabled="!rejectNote.trim()" :loading="store.busy" @click="submitReject">Reject</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import type { ActualsSummary, Booking, Call, Gig, Position, TimesheetEntry, TimesheetEntryStatus } from '@gigdriver/domain';
import { entryHours, isGigLockedForTimesheets, needsAttention } from '@gigdriver/domain';
import { useTimesheetsStore } from '../stores/timesheets';
import { usePeopleStore } from '../stores/people';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';

const props = defineProps<{
  orgId: string;
  gig: Gig;
  calls: Call[];
  positions: Position[];
  bookings: Booking[];
  actuals: ActualsSummary;
}>();

const store = useTimesheetsStore();
const peopleStore = usePeopleStore();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const error = ref<string | null>(null);

// Entries are subscribed by the parent (GigDetail) — it needs them for the
// Staffing Budget too — so this component only reads store.gigEntries.

const locked = computed(() => isGigLockedForTimesheets(props.gig.state));
const isManager = computed(() => ['owner', 'admin', 'manager'].includes(orgStore.myRole ?? ''));
const entries = computed(() => store.gigEntries);
const pendingCount = computed(() => entries.value.filter(e => e.status === 'PENDING').length);
const now = ref(new Date());
const staleEntries = computed(() => entries.value.filter(e => needsAttention(e, now.value)));

// A coordinator can't review their own time (the function enforces it too).
function cannotReviewOwn(e: TimesheetEntry): boolean {
  return orgStore.myRole === 'coordinator' && e.userId === authStore.currentUser?.uid;
}

const committedBookings = computed(() =>
  props.bookings.filter(b => b.state === 'ACCEPTED' || b.state === 'BOOKED' || b.state === 'COMPLETED'),
);
const bookingById = computed(() => new Map(props.bookings.map(b => [b.id, b])));
const positionById = computed(() => new Map(props.positions.map(p => [p.id, p])));
const callById = computed(() => new Map(props.calls.map(c => [c.id, c])));

const personRollup = computed(() =>
  committedBookings.value
    .map(b => {
      const a = props.actuals.byBooking.get(b.id);
      return {
        bookingId: b.id,
        name: personName({ bookingId: b.id, personId: b.personId, personName: b.personName }),
        role: positionById.value.get(b.positionId)?.role ?? '',
        approvedHours: a?.approvedHours ?? 0,
        approvedCost: a?.approvedCost ?? 0,
        pendingHours: a?.pendingHours ?? 0,
      };
    })
    .filter(r => r.approvedHours > 0 || r.pendingHours > 0),
);

function personName(e: Pick<TimesheetEntry, 'bookingId' | 'personId' | 'personName'>): string {
  return (
    e.personName ??
    bookingById.value.get(e.bookingId)?.personName ??
    peopleStore.people.find(p => p.id === e.personId)?.name ??
    e.personId
  );
}
function roleName(e: TimesheetEntry): string {
  const p = positionById.value.get(e.positionId);
  return p ? `${p.role}${p.label ? ` — ${p.label}` : ''}` : '';
}
function callFor(e: TimesheetEntry): Call | undefined {
  return e.callId ? callById.value.get(e.callId) : undefined;
}

const bookingItems = computed(() =>
  committedBookings.value.map(b => ({
    value: b.id,
    title: `${personName({ bookingId: b.id, personId: b.personId, personName: b.personName })} — ${
      positionById.value.get(b.positionId)?.role ?? 'Position'
    }`,
  })),
);
const callItems = computed(() => {
  const b = bookingById.value.get(form.bookingId);
  return (b?.callIds ?? [])
    .map(id => callById.value.get(id))
    .filter((c): c is Call => !!c)
    .map(c => ({ value: c.id, title: `${c.type} — ${formatDateTime(c.startTime)}` }));
});

// --- add / edit --------------------------------------------------------------

const formOpen = ref(false);
const formError = ref<string | null>(null);
const editing = ref<TimesheetEntry | null>(null);
const form = reactive({
  bookingId: '',
  callId: undefined as string | undefined,
  clockIn: '',
  clockOut: '',
  breakMinutes: undefined as number | undefined,
  notes: '',
});

function pad(n: number) {
  return String(n).padStart(2, '0');
}
// datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time; new Date() of that
// string parses back as local — same convention the rest of GigDetail uses.
function toInput(d?: Date): string {
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Choosing a call prefills its scheduled start/end (still editable). Clearing
// the call leaves whatever is typed. Only for a NEW entry — an edit keeps the
// punched times.
function fillFromCall(callId: string | null | undefined) {
  if (editing.value || !callId) return;
  const call = callById.value.get(callId);
  if (!call) return;
  form.clockIn = toInput(call.startTime);
  form.clockOut = toInput(call.endTime);
}

// Times can't be in the future (the server enforces it) — a call that hasn't
// finished yet prefills an end that is, so say so before they hit Add.
const clockOutInFuture = computed(() => !!form.clockOut && new Date(form.clockOut).getTime() > Date.now());
const clockOutHint = computed(() => {
  if (clockOutInFuture.value) return "That's in the future — time can only be entered once it has happened.";
  return editing.value?.status === 'OPEN' ? 'Set a clock-out to close this entry' : '';
});

function openAdd() {
  editing.value = null;
  formError.value = null;
  Object.assign(form, { bookingId: '', callId: undefined, clockIn: '', clockOut: '', breakMinutes: undefined, notes: '' });
  formOpen.value = true;
}

function openEdit(e: TimesheetEntry) {
  editing.value = e;
  formError.value = null;
  Object.assign(form, {
    bookingId: e.bookingId,
    callId: e.callId,
    clockIn: toInput(e.clockIn),
    clockOut: toInput(e.clockOut),
    breakMinutes: e.breakMinutes,
    notes: e.notes ?? '',
  });
  formOpen.value = true;
}

async function saveForm() {
  formError.value = null;
  const clockIn = form.clockIn ? new Date(form.clockIn) : undefined;
  const clockOut = form.clockOut ? new Date(form.clockOut) : undefined;
  const breakMinutes = form.breakMinutes === undefined || (form.breakMinutes as unknown) === '' ? undefined : Number(form.breakMinutes);
  try {
    if (editing.value) {
      await store.updateEntry({
        orgId: props.orgId,
        gigId: props.gig.id,
        entryId: editing.value.id,
        clockIn,
        clockOut,
        breakMinutes: breakMinutes ?? null,
        notes: form.notes.trim() || null,
      });
    } else {
      const booking = bookingById.value.get(form.bookingId);
      if (!booking) return void (formError.value = 'Choose a person.');
      if (!clockIn || !clockOut) return void (formError.value = 'Enter both clock-in and clock-out.');
      await store.createEntry({
        orgId: props.orgId,
        gigId: props.gig.id,
        positionId: booking.positionId,
        bookingId: booking.id,
        callId: form.callId,
        clockIn,
        clockOut,
        breakMinutes,
        notes: form.notes.trim() || undefined,
      });
    }
    formOpen.value = false;
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Failed to save';
    store.error = null;
  }
}

// --- review ------------------------------------------------------------------

async function approve(e: TimesheetEntry) {
  try {
    await store.reviewEntry({ orgId: props.orgId, gigId: props.gig.id, entryId: e.id, decision: 'APPROVED' });
  } catch {
    // surfaced via store.error
  }
}

const rejectOpen = ref(false);
const rejectNote = ref('');
let rejecting: TimesheetEntry | null = null;
function openReject(e: TimesheetEntry) {
  rejecting = e;
  rejectNote.value = '';
  rejectOpen.value = true;
}
async function submitReject() {
  if (!rejecting) return;
  try {
    await store.reviewEntry({
      orgId: props.orgId,
      gigId: props.gig.id,
      entryId: rejecting.id,
      decision: 'REJECTED',
      note: rejectNote.value.trim(),
    });
  } catch {
    // surfaced via store.error
  }
  rejectOpen.value = false;
}

async function reopen(e: TimesheetEntry) {
  try {
    await store.reopenEntry({ orgId: props.orgId, gigId: props.gig.id, entryId: e.id });
  } catch {
    // surfaced via store.error
  }
}

async function confirmDelete(e: TimesheetEntry) {
  if (!window.confirm(`Delete ${personName(e)}'s entry? This can't be undone.`)) return;
  try {
    await store.deleteEntry({ orgId: props.orgId, gigId: props.gig.id, entryId: e.id });
  } catch {
    // surfaced via store.error
  }
}

function dismissError() {
  error.value = null;
  store.error = null;
}

// --- display -----------------------------------------------------------------

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function formatDateTime(d: Date): string {
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${formatTime(d)}`;
}
function statusLabel(s: TimesheetEntryStatus): string {
  return { OPEN: 'Clocked in', PENDING: 'Pending', APPROVED: 'Approved', REJECTED: 'Rejected' }[s];
}
function statusColor(s: TimesheetEntryStatus): string {
  return { OPEN: 'info', PENDING: 'warning', APPROVED: 'success', REJECTED: 'error' }[s];
}
</script>
